import { useContext, useEffect, useLayoutEffect } from "react";
import ColorModeContext from "../../layout/themeContext";
import { normalizeUiPalette } from "../../theme/crmColors";
import api from "../../services/api";

// Authentication owns the company boundary. Palette loading must never block startup.
export default function CompanyPaletteSync({ isAuth, user, socket }) {
  const context = useContext(ColorModeContext);
  const { setUiPalette, getUiPaletteRevision, getUiPaletteReadRevision } = context.colorMode || context;
  const companyId = isAuth ? user?.companyId : null;
  const userId = isAuth ? user?.id : null;

  useLayoutEffect(() => {
    if (setUiPalette) setUiPalette("cchat", false);
  }, [companyId, userId, setUiPalette]);

  useEffect(() => {
    if (!companyId || !userId || !setUiPalette || !getUiPaletteRevision) return;
    let active = true;
    let request = 0;
    const load = async () => {
      const current = ++request;
      const readRevision = getUiPaletteReadRevision || getUiPaletteRevision;
      const revision = readRevision();
      if (revision === null) return;
      try {
        const { data } = await api.get("/settings/uiPalette", { timeout: 15000 });
        if (!active || current !== request || revision !== readRevision()) return;
        if (data && Number(data.companyId) !== Number(companyId)) return;
        setUiPalette(normalizeUiPalette(data?.value));
      } catch (_) {
        // Keep the current palette on transient failures; a new session starts at the default.
      }
    };
    const onSetting = data => {
      if (!active || data?.action !== "update" || data?.setting?.key !== "uiPalette" ||
        Number(data.setting.companyId) !== Number(companyId)) return;
      setUiPalette(normalizeUiPalette(data.setting.value));
    };
    const event = "company-" + companyId + "-settings";
    // A disposed/previous-company socket can still exist briefly while auth updates.
    const currentSocket = socket && !socket.disposed &&
      Number(socket.companyId) === Number(companyId) && Number(socket.userId) === Number(userId) ? socket : null;
    if (currentSocket && typeof currentSocket.on === "function") {
      currentSocket.on(event, onSetting);
      currentSocket.on("connect", load);
    }
    load();
    return () => {
      active = false;
      if (currentSocket && typeof currentSocket.off === "function") {
        currentSocket.off(event, onSetting);
        currentSocket.off("connect", load);
      }
    };
  }, [companyId, userId, socket, setUiPalette, getUiPaletteRevision, getUiPaletteReadRevision]);
  return null;
}
